import appPreloadQuery, {
  type AppPreloadQuery,
} from '#app/__generated__/relay/AppPreloadQuery.graphql.js';
import { createRelayEnvironment } from '#app/createRelayEnvironment.js';
import { localStorage } from '#app/services/storage.js';
import { type PageContext } from '../src/contexts/PageContextContext/PageContextContext.js';
import { parseJson } from '../src/utilities/parseJson.js';
import { createContraPublicUtilities } from './createContraPublicUtilities.js';
import { PageTransition } from './PageTransition.js';
import { printHelloConsoleBanner } from './printHelloConsoleBanner.js';
import { loadQuery } from '@contra/relay';
import { createSentry } from '@contra/sentry/react';
import { createLogWriter } from '@roarr/browser-log-writer';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { loadStripe } from '@stripe/stripe-js/pure';
import { domAnimation, LazyMotion, MotionConfig } from 'framer-motion';
import { isbot } from 'isbot';
import { createRoot, type Root } from 'react-dom/client';

const navigatorIsBot = isbot(navigator.userAgent);

if (navigatorIsBot) {
  // We want to see these logs in Google Console
  // eslint-disable-next-line no-console
  console.info('detected bot');
} else {
  // We want to see these logs in Google Console
  // eslint-disable-next-line no-console
  console.info('detected regular user');
}

let root: Root | undefined;

let initialRender = true;

let relayEnvironment: ReturnType<typeof createRelayEnvironment>;

const analytics = new AnalyticsBrowser();

export const onRenderClient = async (pageContext: PageContext) => {
  const appConfiguration = pageContext.publicAppConfiguration;

  if (!relayEnvironment) {
    relayEnvironment = createRelayEnvironment({
      apiUrl: appConfiguration.apiUrl,
      relayRecordMap: appConfiguration.relayRecordMap,
      releaseVersion: appConfiguration.releaseVersion ?? undefined,
    });
  }

  if (initialRender) {
    window.contra = createContraPublicUtilities();

    printHelloConsoleBanner();

    if (appConfiguration.enableLogs) {
      localStorage.setItem('ROARR_LOG', 'true', {
        ignoreNamingConventions: true,
      });
    }

    if (
      localStorage.getItem('ROARR_LOG', {
        ignoreNamingConventions: true,
      }) === 'true'
    ) {
      globalThis.ROARR = globalThis.ROARR ?? {};

      globalThis.ROARR.write = createLogWriter({
        styleOutput: !navigatorIsBot,
      });
    }

    if (appConfiguration.sentry) {
      // Sentry must be initialized early in order to inject tracing headers.
      createSentry(appConfiguration.sentry);
    }

    if (appConfiguration.enableSpotlight) {
      import('@spotlightjs/overlay').then((Spotlight) => {
        // @ts-expect-error - ESM issue
        Spotlight.init({ injectImmediately: true });
      });
    }
  }

  if (appConfiguration.disableAnalytics) {
    loadStripe.setLoadParameters({ advancedFraudSignals: false });
  }

  const preloadedQuery = loadQuery<AppPreloadQuery>(
    relayEnvironment,
    appPreloadQuery,
    {},
    {
      fetchPolicy: 'store-and-network',
    },
  );

  let pageQuery;

  if (pageContext.exports.pageConcreteRequest) {
    const { routeParams } = pageContext;

    const queryVariables = routeParams?.['queryVariables']
      ? parseJson(routeParams?.['queryVariables'])
      : {};

    pageQuery = loadQuery(
      relayEnvironment,
      pageContext.exports.pageConcreteRequest,
      queryVariables,
      {
        // We are setting 'store-or-network' on initial render in order to re-use server-side provided record map.
        // However, we are using 'store-and-network' as the default when navigating pages to avoid serving stale data.
        fetchPolicy: initialRender ? 'store-or-network' : 'store-and-network',
      },
    );
  }

  const { Page } = pageContext;

  const page = (
    <MotionConfig reducedMotion="user">
      <LazyMotion
        features={domAnimation}
        strict
      >
        <PageTransition
          analytics={analytics}
          appConfiguration={appConfiguration}
          pageContext={pageContext}
          pageRef={<Page preloaded={pageQuery} />}
          preloadedQuery={preloadedQuery}
          relayEnvironment={relayEnvironment}
        />
      </LazyMotion>
    </MotionConfig>
  );

  if (!root) {
    const container = document.querySelector('#root');

    if (!container) {
      throw new Error('Could not find "#root" element to render React.');
    }

    root = createRoot(container);

    initialRender = false;
  }

  root.render(page);
};
